import React, { useState } from "react";
import CustomModal from "../../components/CustomModal";
import { useCreatePartner } from "./hooks";
import { useT } from "../../i18n";

interface NewPartnerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (createdPartner: any) => void;
}

const partnershipLevels = ["level1", "level2", "level3"];

const NewPartnerModal: React.FC<NewPartnerModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const t = useT();
  const [companyName, setCompanyName] = useState("");
  const [companyVatId, setCompanyVatId] = useState("");
  const [partnershipLevel, setPartnershipLevel] = useState<string>("level1");
  const [error, setError] = useState("");

  const createPartnerMutation = useCreatePartner();

  const resetForm = () => {
    setCompanyName("");
    setCompanyVatId("");
    setPartnershipLevel("level1");
    setError("");
  };

  const handleSave = async () => {
    setError("");
    if (!companyName || !companyVatId) {
      setError(t("users.newPartnerModal.errors.requiredFields"));
      return;
    }

    try {
      const result = await createPartnerMutation.mutateAsync({
        companyName,
        companyVatId,
        partnershipLevel,
      });

      resetForm();
      onClose();
      onSuccess?.(result.data || result);
    } catch (err: any) {
      setError(t("users.newPartnerModal.errors.createFailed"));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <div
        className="w-[520px] max-w-full h-full flex flex-col mx-auto"
        style={{ height: "100%" }}
      >
        <div className="flex flex-col w-full h-full" style={{ height: "100%" }}>
          <div className="flex items-center gap-3 pt-8 pb-2 border-b border-gray-200 w-full pl-0 pr-0">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-lg ml-0"
              style={{ backgroundColor: "#7E3AF220" }}
            >
              <i
                className="fa-solid fa-building text-2xl"
                style={{ color: "#7E3AF2" }}
              ></i>
            </div>
            <div className="ml-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("users.newPartnerModal.title")}
              </h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {t("users.newPartnerModal.subtitle")}
              </div>
            </div>
          </div>

          <form
            className="py-6 space-y-5 w-full flex flex-col justify-center px-0 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newPartnerModal.companyNameLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-200"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t("users.newPartnerModal.companyNamePlaceholder")}
                required
              />
            </div>

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newPartnerModal.vatIdLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-200"
                value={companyVatId}
                onChange={(e) => setCompanyVatId(e.target.value)}
                placeholder={t("users.newPartnerModal.vatIdPlaceholder")}
                required
              />
            </div>

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newPartnerModal.partnershipLevelLabel")}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-200"
                value={partnershipLevel}
                onChange={(e) => setPartnershipLevel(e.target.value)}
              >
                {partnershipLevels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {t(`users.partnershipLevels.${lvl}`)}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="text-red-600 text-sm font-medium mt-2 px-0">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 px-0">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                onClick={handleClose}
                disabled={createPartnerMutation.isPending}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg text-white hover:opacity-90 font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#7E3AF2" }}
                disabled={createPartnerMutation.isPending}
              >
                {createPartnerMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    {t("common.loading.creating")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-building"></i>
                    {t("users.newPartnerModal.submit")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default NewPartnerModal;
